import React, { useEffect, useRef, useState } from 'react';
import { register, notify, logData, pingServer } from '../../api';
import ShareBox from './ShareBox';
import { trackEvent } from './track';

// Formularul de înscriere folosit în hero și în blocul final.
// Păstrează evenimentele de logare existente: openForm (la prima tastă) și submitNew.
const SignupForm = ({ id, onSubmitted, dark = false, otherDone = false }) => {
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [consent, setConsent] = useState(false);
    const [status, setStatus] = useState('idle'); // idle | sending | done | exists | error
    const [warning, setWarning] = useState('');
    const openedRef = useRef(false);
    const nameRef = useRef(null);
    const emailRef = useRef(null);
    const consentRef = useRef(null);
    const doneRef = useRef(null);
    const errorRef = useRef(null);

    const logOpen = () => {
        if (openedRef.current) return;
        openedRef.current = true;
        const referrer = document.referrer || "No referrer";
        logData("openForm", { referrer: referrer, id: id }).catch(() => { });
        trackEvent('form_start', { form_id: id });
        // Trezește serverul cât timp omul completează formularul.
        pingServer();
    };

    const warn = (text, ref) => {
        setWarning(text);
        if (ref && ref.current) ref.current.focus();
    };

    useEffect(() => {
        if ((status === 'done' || status === 'exists') && doneRef.current) doneRef.current.focus();
        if (status === 'error' && errorRef.current) errorRef.current.focus();
    }, [status]);

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (!name.trim()) {
            warn('Scrie-ți numele, ca să știu cum să ți mă adresez în emailuri.', nameRef);
            return;
        }
        if (!/^\S+@\S+\.\S+$/.test(email.trim())) {
            warn('Adresa de email nu pare completă. Verific-o, te rog.', emailRef);
            return;
        }
        if (!consent) {
            warn('Bifează acordul de mai sus ca să-ți pot trimite lecțiile.', consentRef);
            return;
        }
        setWarning('');
        setStatus('sending');
        try {
            await register(name.trim(), email.trim());
            setStatus('done');
            const referrer = document.referrer || "No referrer";
            logData("submitNew", { referrer: referrer, id: id }).catch(() => { });
            // „sign_up” poate fi marcat în GA4 ca eveniment cheie (key event).
            trackEvent('sign_up', { method: 'email', form_id: id });
            if (onSubmitted) onSubmitted(id);
        } catch (error) {
            if (error && error.response && error.response.status === 409) {
                setStatus('exists');
                logData("submitExisting", { id: id }).catch(() => { });
                if (onSubmitted) onSubmitted(id);
                return;
            }
            setStatus('error');
            notify("REGISTER_ERROR: " + name + " " + email + " " + window.location);
        }
    };

    if (status === 'done' || status === 'exists') {
        return (
            <div className={`lp-form lp-form--done ${dark ? 'lp-form--dark' : ''}`} role="status" ref={doneRef} tabIndex="-1">
                {status === 'done' ? (
                    <>
                        <p className="lp-form__done-title">Gata, te-ai înscris.</p>
                        <p>Îți trimit două emailuri: unul cu <b>Lecția 1 (PDF atașat)</b> și unul cu un <b>link de confirmare</b>. Apasă pe link ca să primești și restul lecțiilor.</p>
                        <p className="lp-small">
                            Vin de la <b>curs@mail.aicursgratuit.ro</b>. Nu le vezi în câteva minute? Caută în Spam sau în Promoții.
                        </p>
                    </>
                ) : (
                    <>
                        <p className="lp-form__done-title">Adresa asta e deja înscrisă.</p>
                        <p>Lecțiile vin pe aceeași adresă. Dacă nu le găsești, caută în Spam sau în Promoții un email cu „AICursGratuit” în subiect.</p>
                    </>
                )}
                <ShareBox />
            </div>
        );
    }

    const sending = status === 'sending';

    if (otherDone) {
        return (
            <div className={`lp-form lp-form--done ${dark ? 'lp-form--dark' : ''}`}>
                <p className="lp-form__done-title">Gata, te-ai înscris. Mulțumesc!</p>
                <p className="lp-small">Verifică-ți emailul (și Spam sau Promoții).</p>
            </div>
        );
    }

    return (
        <form className={`lp-form ${dark ? 'lp-form--dark' : ''}`} onSubmit={handleSubmit} noValidate>
            <div className="lp-form__fields">
                <label className="lp-visually-hidden" htmlFor={`name-${id}`}>Numele tău</label>
                <input
                    id={`name-${id}`}
                    ref={nameRef}
                    className="lp-input"
                    type="text"
                    autoComplete="name"
                    placeholder="Numele tău"
                    value={name}
                    onChange={(e) => { logOpen(); setName(e.target.value); setWarning(''); }}
                    disabled={sending}
                />
                <label className="lp-visually-hidden" htmlFor={`email-${id}`}>Adresa ta de email</label>
                <input
                    id={`email-${id}`}
                    ref={emailRef}
                    className="lp-input"
                    type="email"
                    inputMode="email"
                    autoComplete="email"
                    placeholder="Adresa ta de email"
                    value={email}
                    onChange={(e) => { logOpen(); setEmail(e.target.value); setWarning(''); }}
                    disabled={sending}
                />
            </div>
            <label className="lp-consent" htmlFor={`consent-${id}`}>
                <input
                    id={`consent-${id}`}
                    ref={consentRef}
                    type="checkbox"
                    checked={consent}
                    onChange={(e) => { setConsent(e.target.checked); setWarning(''); }}
                    disabled={sending}
                />
                <span>
                    Vreau lecțiile și newsletterul pe email.
                    Accept <a href="/#/terms" target="_blank" rel="noopener noreferrer">termenii</a> și{' '}
                    <a href="/#/confidentiality" target="_blank" rel="noopener noreferrer">politica de confidențialitate</a>.
                </span>
            </label>
            {warning && <p className="lp-form__warning" role="alert" id={`warn-${id}`}>{warning}</p>}
            {status === 'error' && (
                <p className="lp-form__warning" role="alert" ref={errorRef} tabIndex="-1">
                    Ceva nu a mers. Încearcă din nou peste un minut sau scrie-mi prin butonul „Întreabă-mă orice” din partea de sus.
                </p>
            )}
            <button type="submit" className="lp-btn lp-btn--cta lp-btn--block" disabled={sending}>
                {sending ? 'Se trimite…' : 'Trimite-mi Lecția 1'}
            </button>
        </form>
    );
};

export default SignupForm;
